import { useMemo } from 'react';
import type { FunctionComponent } from 'react';
import styled from 'styled-components';
import { Trans, useTranslation } from 'react-i18next';
import Dialog, { Clip, Next } from '../molecules/Dialog';
import Image from '../atoms/Image';
import SpeechBubble from '../atoms/SpeechBubble';
import AutofitText from '../molecules/AutofitText';
import Text from '../atoms/Text';
import Counter from '../atoms/Counter';
import AspectPoints from '../molecules/AspectPoints';
import { getConceptScore } from '../../data/Concepts';
import type { Concept } from '../../data/Concepts';
import { mapScorePoints } from '../../data/Score';
import Assets from '../../assets/Assets';

const Title = styled.div`
	font: ${(props) => props.theme.font.title};
	font-size: 3.6em;
`;

const Scores = styled.div`
	color: ${(props) => props.theme.colors.darkBlue};
	display: flex;
	flex-wrap: nowrap;
	justify-content: center;
	& > * {
		margin: 0.8rem 1.4rem;
	}
`;

export interface Props {
	concept: Concept;
	onNext: (concept: Concept) => void;
}

const ResultDialog: FunctionComponent<Props> = (props: Props) => {
	const { concept, onNext } = props;
	const { t } = useTranslation();

	const conceptScore = useMemo(() => getConceptScore(concept), [concept]);

	const points = mapScorePoints(conceptScore, (aspect, points) => (
		<AspectPoints key={aspect} aspect={aspect}>
			<Counter to={points} />
		</AspectPoints>
	));

	return (
		<Dialog>
			<Clip>
				<Image
					src={Assets.images['narrator.amazed.body']}
					width="38vmin"
					height="56vmin"
					position="absolute"
					left="10%"
					bottom="0"
				/>
				<SpeechBubble
					anchor="left_bottom"
					shadow
					position="absolute"
					left="calc(10% + 38vmin)"
					right="6%"
					top="8%"
					bottom="20vmin"
				>
					<AutofitText>
						<Title>
							{concept && <Text content={t(concept.texts.resultTitle)} />}
						</Title>
						<Scores>{points}</Scores>
					</AutofitText>
				</SpeechBubble>
			</Clip>
			<Next onClick={() => onNext(concept)}>
				<Trans>concept_result.next</Trans>
			</Next>
		</Dialog>
	);
};

export default ResultDialog;
